import React, { useMemo, useState } from 'react';
import { Chip, Input } from '@heroui/react';
import { Typography } from '../../../../components';
import { SearchInputIc } from '../../../../components/icons';
import { COUNTRY_CONTINENT } from '../../../../utils/country-continent';

interface BlockedCountriesTooltipProps {
  countries: string[];
  offerShortId: string;
  modelUsername: string;
}

type ContinentCode = 'AF' | 'AN' | 'AS' | 'EU' | 'NA' | 'OC' | 'SA' | 'OTHER';

const CONTINENT_ORDER: ContinentCode[] = ['NA', 'SA', 'EU', 'AS', 'AF', 'OC', 'AN', 'OTHER'];

const CONTINENT_NAMES: Record<ContinentCode, string> = {
  NA: 'Северная Америка',
  SA: 'Южная Америка',
  EU: 'Европа',
  AS: 'Азия',
  AF: 'Африка',
  OC: 'Океания',
  AN: 'Антарктида',
  OTHER: 'Другое',
};

const createDisplayNames = (locale: string) => {
  try {
    return new Intl.DisplayNames([locale], { type: 'region' });
  } catch {
    return null;
  }
};

const getFlagUrl = (code: string) => {
  if (code.length !== 2) return null;
  return `https://flagcdn.com/16x12/${code.toLowerCase()}.png`;
};

const BlockedCountriesTooltip: React.FC<BlockedCountriesTooltipProps> = ({
  countries,
  offerShortId,
  modelUsername,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  const russianDisplay = useMemo(() => createDisplayNames('ru'), []);
  const englishDisplay = useMemo(() => createDisplayNames('en'), []);

  const normalizedCountries = useMemo(() => {
    return countries
      .map((countryCode) => {
        const code = countryCode.toUpperCase();
        const continent = ((COUNTRY_CONTINENT as Record<string, string>)[code] ?? 'OTHER') as ContinentCode;
        const ruName = russianDisplay?.of(code) ?? code;
        const enName = englishDisplay?.of(code) ?? code;

        return {
          code,
          continent,
          flagUrl: getFlagUrl(code),
          names: {
            primary: ruName,
            en: enName,
          },
        };
      })
      .sort((a, b) => a.names.primary.localeCompare(b.names.primary));
  }, [countries, russianDisplay, englishDisplay]);

  const filteredCountries = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return normalizedCountries;

    return normalizedCountries.filter((country) =>
      [country.names.primary, country.names.en, country.code]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query))
    );
  }, [normalizedCountries, searchQuery]);

  const groupedCountries = useMemo(() => {
    const groups: Record<ContinentCode, typeof normalizedCountries> = {
      AF: [],
      AN: [],
      AS: [],
      EU: [],
      NA: [],
      OC: [],
      SA: [],
      OTHER: [],
    };

    filteredCountries.forEach((country) => {
      const targetGroup = groups[country.continent] ? country.continent : 'OTHER';
      groups[targetGroup].push(country);
    });

    return groups;
  }, [filteredCountries]);

  return (
    <div className="flex w-[640px] max-w-[640px] flex-col gap-4 p-4">
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-[8px]">
            <Typography variant="h6" className="text-[#11181C] text-[16px] leading-[28px] font-medium">
              Заблокированные страны
            </Typography>
            <Chip
              classNames={{
                base: 'bg-[#F4F4F5] h-[24px] rounded-lg',
                content: 'text-sm font-medium !text-[#71717A]',
              }}
            >
              {countries.length}
            </Chip>
          </div>
          <div className="flex items-center gap-2">
            <Typography variant="body2" className="text-[#11181C] font-medium">
              {offerShortId}
            </Typography>
            <a
              href={`https://onlyfans.com/${modelUsername}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#006FEE] text-sm font-medium hover:underline"
            >
              @{modelUsername}
            </a>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-[8px]">
        <Typography variant="body2" className="text-[#11181C] font-medium text-xs">
          Страна
        </Typography>
        <Input
          type="text"
          placeholder="Поиск страны..."
          size="sm"
          value={searchQuery}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
          endContent={<SearchInputIc className="text-[#A1A1AA]" />}
          classNames={{
            base: 'w-full',
            input: 'text-sm text-[#11181C] placeholder:text-[#A1A1AA]',
            inputWrapper: 'rounded-xl h-[36px] bg-[#F4F4F5] border border-transparent',
          }}
        />
      </div>

      <div className="flex max-h-[320px] flex-col gap-5 overflow-y-auto pr-1">
        {CONTINENT_ORDER.map((continent) => {
          const group = groupedCountries[continent];
          if (!group || group.length === 0) return null;

          return (
            <div key={continent} className="flex flex-col gap-[16px]">
              <Typography variant="body2" className="text-[#11181C] !font-medium !text-[14px]">
                {CONTINENT_NAMES[continent]}
              </Typography>
              <div className="grid grid-cols-3 gap-x-4 gap-y-[16px]">
                {group.map((country) => (
                  <div
                    key={country.code}
                    className="flex items-center gap-4 h-[16px] rounded-lg border border-transparent"
                  >
                    {country.flagUrl ? (
                      <img
                        src={country.flagUrl}
                        alt={country.code}
                        width={16}
                        height={16}
                        className="rounded-[4px] border border-[#E4E4E7]/60"
                      />
                    ) : (
                      <span className="text-sm font-medium text-[#A1A1AA]">{country.code}</span>
                    )}
                    <Typography variant="body2" className="text-[#11181C]">
                      {country.names.primary}
                    </Typography>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {filteredCountries.length === 0 && (
          <div className="rounded-lg border border-[#E4E4E7] px-3 py-4 text-center">
            <Typography variant="body2" className="text-[#71717A]">
              Страны не найдены
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlockedCountriesTooltip;
